import { store } from './store.js';
// ========================================
// PASO 3: REPRESENTACIÓN VISUAL INTERACTIVA
// ========================================
import { wizardState, getString } from './wizardState.js';
import { safeSetHTML } from './security.js';

let barValues = []; // Store value for each bar
let maxBarValue = 20;
let draggingBarIndex = null;
let barTargets = [];

export function initializeRepresentation() {
    if (!store.state || !store.state.problems) return;

    const problem = store.state.problems[store.state.currentProblem];
    const usefulData = problem.data.useful;

    // Obtener TODOS los elementos (uno por cada dato)
    const objectNames = window.getAllObjectNames ? window.getAllObjectNames(problem) : ['unidades'];

    // Retrieve saved texts from Step 2 (to preserve EXACT phrasing like "25 zumos de naranja")
    const preservedTexts = wizardState.data.usefulDataTexts || [];

    Logger.info('=== STEP 3: Initialize Representation ===');

    // 1. Calculate Raw Max
    const rawMax = Math.max(...usefulData, 20);

    // 2. Calculate Scale Max (Rounded to next interval)
    let interval;
    if (rawMax <= 10) interval = 1;
    else if (rawMax <= 20) interval = 5;
    else if (rawMax <= 30) interval = 5;
    else if (rawMax <= 50) interval = 10;
    else if (rawMax <= 100) interval = 20;
    else interval = 25;

    // Set GLOBAL maxBarValue to the Scale Max
    maxBarValue = (Math.ceil(rawMax / interval) * interval);
    if (maxBarValue === rawMax) maxBarValue += interval; // Add breathing room if exact

    // Reset state
    barValues = new Array(usefulData.length).fill(0);
    barTargets = [...usefulData];

    // Create bars container
    const container = document.getElementById('barsContainer');
    if (!container) return;
    safeSetHTML(container, '');

    // Create one bar for each useful data point
    usefulData.forEach((targetValue, index) => {
        // Usar el nombre de elemento correspondiente a este dato
        let labelUse = null;

        // Priority 1: Use preserved full text from Step 2
        if (preservedTexts[index]) {
            labelUse = preservedTexts[index];
        }
        // Priority 2: Try to extract full phrase from problem statement using getAllDataPhrases
        else if (window.getAllDataPhrases) {
            const autoPhrases = window.getAllDataPhrases(problem);
            if (autoPhrases[index]) {
                labelUse = autoPhrases[index];
            } else {
                // Final fallback: reconstruct from object name
                const elementName = objectNames[index] || objectNames[0] || 'unidades';
                labelUse = `${targetValue} ${elementName}`;
            }
        }
        // Priority 3: Basic fallback using object names
        else {
            const elementName = objectNames[index] || objectNames[0] || 'unidades';
            labelUse = `${targetValue} ${elementName}`;
        }

        const barDiv = createInteractiveBar(index, targetValue, labelUse, maxBarValue, true);
        container.appendChild(barDiv);
    });

    Logger.info('📊 Representación inicializada:', usefulData.length, 'barras. Max Escala:', maxBarValue);
}

function createInteractiveBar(index, targetValue, labelOrName, maxValue, isFullText = false) {
    const barWrapper = document.createElement('div');
    barWrapper.className = 'bar-wrapper';

    // Número de barra (1-indexed para mostrar al usuario)
    const barNumber = index + 1;

    // Generar texto de instrucción con concordancia singular/plural
    let labelText;
    const datoLabel = `<span class="bar-dato-label">DATO ${barNumber}:</span>`;

    if (isFullText) {
        // Use the full preserved text directly
        labelText = `${datoLabel} ${labelOrName}. Desplaza el círculo rojo hasta que alcance el valor ${targetValue}.`;
    } else {
        // Fallback: Reconstruct from object name
        const objectName = labelOrName;
        if (targetValue === 1) {
            // Para valor 1, usar forma singular
            let singularName = objectName;
            if (objectName.endsWith('es')) {
                singularName = objectName.slice(0, -2);
            } else if (objectName.endsWith('s')) {
                singularName = objectName.slice(0, -1);
            }
            labelText = `${datoLabel} 1 ${singularName}. Desplaza el círculo rojo hasta que alcance el valor 1.`;
        } else {
            labelText = `${datoLabel} ${targetValue} ${objectName}. Desplaza el círculo rojo hasta que alcance el valor ${targetValue}.`;
        }
    }

    safeSetHTML(barWrapper, `
        <div class="bar-label">${labelText}</div>
        <div class="bar-target-display" style="text-align: center; margin-bottom: 15px; font-size: 1.2rem; color: #475569;">
            LLEGA HASTA: <strong style="font-size: 1.5rem; color: #ef4444; background: #fee2e2; padding: 4px 12px; border-radius: 12px;">${targetValue}</strong>
        </div>

        <div class="bar-interactive">
            <div class="bar-track-with-scale" id="barTrack${index}">
                <div id="barHandle${index}" class="bar-handle" style="left: 0%; width: 44px; height: 44px; top: -12px; box-shadow: 0 4px 12px rgba(239, 68, 68, 0.5); border: 3px solid white;">
                    <span id="barValue${index}" class="bar-value-display" style="top: -30px; font-size: 1.2rem;">0</span>
                </div>
                <div class="integrated-scale" id="scale${index}"></div>
            </div>
        </div>
    `);

    // Generate integrated scale (using the PRE-CALCULATED maxValue)
    setTimeout(() => {
        generateIntegratedScale(`scale${index}`, maxValue);
        setupSingleBarDragging(index, maxValue);
    }, 0);

    return barWrapper;
}

function generateIntegratedScale(scaleId, maxValue) {
    const scaleDiv = document.getElementById(scaleId);
    if (!scaleDiv) return;

    scaleDiv.innerHTML = '';

    // Re-determine interval for rendering ticks
    let interval;
    if (maxValue <= 10) interval = 1;
    else if (maxValue <= 20) interval = 5;
    else if (maxValue <= 30) interval = 5;
    else if (maxValue <= 50) interval = 10;
    else if (maxValue <= 100) interval = 20;
    else interval = 25;

    // Generate tick marks up to maxValue
    for (let i = 0; i <= maxValue; i++) {
        const percentage = (i / maxValue) * 100;

        // 1. Tick Mark
        const tick = document.createElement('div');
        tick.className = 'scale-tick';

        if (i % interval === 0) {
            tick.classList.add('scale-tick-major');
        } else {
            tick.classList.add('scale-tick-minor');
        }
        tick.style.left = `${percentage}%`;
        scaleDiv.appendChild(tick);

        // 2. Label
        if (i % interval === 0) {
            const span = document.createElement('span');
            span.className = 'scale-number';
            span.textContent = i;
            span.style.position = 'absolute';
            span.style.left = `${percentage}%`;
            span.style.transform = 'translateX(-50%)';
            scaleDiv.appendChild(span);
        }
    }
}

function setupSingleBarDragging(index, maxValue) {
    const barTrack = document.getElementById(`barTrack${index}`);
    const barHandle = document.getElementById(`barHandle${index}`);

    if (!barTrack || !barHandle) return;

    // Mouse events
    barHandle.addEventListener('mousedown', (e) => startBarDrag(e, index));
    // Touch events for mobile con passive: false para permitir preventDefault
    barHandle.addEventListener('touchstart', (e) => startBarDrag(e, index), { passive: false });

    // Click on track to move handle
    barTrack.addEventListener('click', (e) => {
        // Ignorar si el click fue en el handle
        if (e.target === barHandle || barHandle.contains(e.target)) return;

        const rect = barTrack.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const value = Math.round((x / rect.width) * maxValue);

        updateSingleBarPosition(index, Math.max(0, Math.min(value, maxValue)), maxValue);
    });
}

function startBarDrag(e, index) {
    draggingBarIndex = index;
    if (e.cancelable) e.preventDefault();

    document.addEventListener('mousemove', dragBar);
    document.addEventListener('mouseup', endBarDrag);
    document.addEventListener('touchmove', dragBar, { passive: false });
    document.addEventListener('touchend', endBarDrag);
    document.addEventListener('touchcancel', endBarDrag);
}

function dragBar(e) {
    if (draggingBarIndex === null) return;

    if (e.cancelable) e.preventDefault();
    const barTrack = document.getElementById(`barTrack${draggingBarIndex}`);
    if (!barTrack) return;

    const rect = barTrack.getBoundingClientRect();
    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    let x = clientX - rect.left;

    x = Math.max(0, Math.min(x, rect.width));

    const rawValue = (x / rect.width) * maxBarValue;
    let value = Math.round(rawValue);
    
    // Magnetic Snap
    const target = barTargets[draggingBarIndex];
    if (target !== undefined && Math.abs(value - target) <= (maxBarValue <= 20 ? 1 : 2)) {
        value = target;
        if (barValues[draggingBarIndex] !== value) {
            if (navigator.vibrate) navigator.vibrate(50);
        }
    }
    const rawPercentage = (x / rect.width) * 100;

    updateSingleBarPosition(draggingBarIndex, Math.max(0, Math.min(value, maxBarValue)), maxBarValue, rawPercentage);
}

function endBarDrag() {
    draggingBarIndex = null;
    document.removeEventListener('mousemove', dragBar);
    document.removeEventListener('mouseup', endBarDrag);
    document.removeEventListener('touchmove', dragBar);
    document.removeEventListener('touchend', endBarDrag);
    document.removeEventListener('touchcancel', endBarDrag);
}

function updateSingleBarPosition(index, value, maxValue, handlePercentage = null) {
    barValues[index] = value;
    const barHandle = document.getElementById(`barHandle${index}`);
    const barTrack = document.getElementById(`barTrack${index}`);
    const barValueSpan = document.getElementById(`barValue${index}`);

    if (!barHandle || !barTrack) return;

    let barFill = barTrack.querySelector('.bar-fill');
    if (!barFill) {
        barFill = document.createElement('div');
        barFill.className = 'bar-fill';
        barTrack.insertBefore(barFill, barHandle);
    }

    const snappedPercentage = (value / maxValue) * 100;
    const finalHandlePos = handlePercentage !== null ? handlePercentage : snappedPercentage;

    barHandle.style.left = `${finalHandlePos}%`;
    barFill.style.width = `calc(${snappedPercentage}% + 6px)`;

    if (barValueSpan) {
        if (barValueSpan.textContent !== value.toString()) {
            if (window.playPop) window.playPop();
            barValueSpan.textContent = value;
        }
    }
}

export function validateRepresentation() {
    const problem = store.state.problems[store.state.currentProblem];
    const usefulData = [...problem.data.useful].sort((a, b) => a - b);
    const sortedBarValues = [...barValues].sort((a, b) => a - b);

    // Compare sorted arrays
    const valuesMatch = JSON.stringify(sortedBarValues) === JSON.stringify(usefulData);

    if (valuesMatch) {
        // Guardar resultados en el estado global del wizard
        wizardState.data.representation = {
            bar1: barValues[0] || 0,
            bar2: barValues[1] || 0
        };

        window.showWizardFeedback(getString('feedbackGreat'), true, true);
        window.markStepComplete(3);

        setTimeout(() => {
            window.routeNextWizardStep(3);
        }, 1500);
    } else {
        window.showWizardFeedback(
            getString('feedbackValuesAre', { values: usefulData.join(' y ') }),
            false
        );
    }
}
